import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatPrice } from "@/data/catalog";
import { adminOrders, adminUpdateOrderStatus } from "@/lib/api";

const STATUSES = ["Pending", "Awaiting Payment", "Paid", "Processing", "Ready", "Completed", "Cancelled"];
const STATUS_COLORS = {
  Pending: "bg-slate-200 text-slate-700",
  "Awaiting Payment": "bg-amber-100 text-amber-700",
  Paid: "bg-green-100 text-green-700",
  Processing: "bg-sky-100 text-sky-700",
  Ready: "bg-indigo-100 text-indigo-700",
  Completed: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-700",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminOrders().then(setOrders).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    await adminUpdateOrderStatus(id, status);
    toast.success("Order status updated");
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
  };

  if (loading) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (orders.length === 0) return <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">No orders yet.</div>;

  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} className="rounded-lg border border-slate-200 bg-white p-5" data-testid={`admin-order-${o.orderNumber}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-slate-900">{o.orderNumber}</span>
                {o.isQuote && <Badge variant="outline" className="rounded-sm text-[10px]">Quote</Badge>}
                <span className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[o.status] || "bg-slate-200"}`}>{o.status}</span>
              </div>
              <div className="mt-1 text-sm text-slate-600">{o.customer.firstName} {o.customer.lastName} · {o.customer.email} · {o.customer.phone}</div>
              <div className="text-xs text-slate-400">
                {new Date(o.createdAt).toLocaleString()} · {o.fulfillment}
                {o.distanceMiles ? ` · ~${o.distanceMiles} mi` : ""}
                {" · pay: "}{o.paymentMethod} ({o.paymentStatus})
              </div>

              {o.fulfillment === "delivery" && o.billing?.address && (
                <div className="mt-2 text-sm text-slate-700">
                  <span className="font-semibold">Delivery Address: </span>
                  {o.billing.address}, {o.billing.city}, {o.billing.state} {o.billing.zip}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-heading text-lg font-black text-slate-900">{o.isQuote ? "Quote" : formatPrice(o.total)}</span>
              <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                <SelectTrigger className="w-[160px] rounded-sm" data-testid={`order-status-${o.orderNumber}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 border-t border-slate-100 pt-3">
            <ul className="space-y-1 text-sm text-slate-600">
              {o.items.map((it, i) => (
                <li key={i} className="flex justify-between">
                  <span><span className="font-mono text-xs text-slate-400">{it.model}</span> — {it.name} × {it.qty}</span>
                  <span>{it.lineTotal != null ? formatPrice(it.lineTotal) : "Request"}</span>
                </li>
              ))}
            </ul>
            {o.notes && <p className="mt-2 text-xs text-slate-500">Notes: {o.notes}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
